import { makeObservable, observable, action } from "mobx";
import type { Nullable, NumericDictionary } from "models-webgl2";
import { Mat3 } from "utils/math/mat3";
import { deepObserve } from "utils/mobx/deep-observe";
import { EventEmitter } from "utils/helpers/classes/event-emitter";
import { Style } from "./style";
import { Transform } from "./transform";
import {
  Webgl2ComponentEmitter,
  Webgl2ComponentExtendPropsType,
  Webgl2ComponentExtendStateType,
  Webgl2ComponentSetStateType,
  Webgl2ComponentGetRenderDataOutput,
} from "./webgl2-component.types";

export abstract class Webgl2Component<
  P extends Webgl2ComponentExtendPropsType | undefined = undefined,
  S extends Nullable<Webgl2ComponentExtendStateType> = undefined,
> {
  public readonly id: number;
  public readonly u_id: [number, number, number, number];

  @observable private _forceChangeToggle = false;

  @observable public readonly state: S;
  @observable public readonly transform: Transform;
  @observable public readonly style: Style;

  public readonly emitter = new EventEmitter<Webgl2ComponentEmitter>();

  public get events() {
    return this.emitter.events;
  }

  protected _parent: Nullable<Webgl2Component<any, any>>;
  public get parent() {
    return this._parent;
  }

  protected readonly _children = new Set<Webgl2Component<any, any>>([]);
  public get hasChildren() {
    return !!this._children.size;
  }

  protected _shouldCalcModel = true;
  public _parentMatrix: Float32Array;
  public _modelMatrix: Float32Array;

  constructor(initialState: Webgl2ComponentSetStateType<S> = null as any) {
    this.id = ++Webgl2Component._id;
    this.u_id = [
      ((this.id >> 0) & 0xff) / 0xff,
      ((this.id >> 8) & 0xff) / 0xff,
      ((this.id >> 16) & 0xff) / 0xff,
      ((this.id >> 24) & 0xff) / 0xff,
    ];

    this.state = initialState;
    this.transform = new Transform();
    this.style = new Style();

    makeObservable(this);

    deepObserve(this, () => (this._shouldCalcModel = true));

    Webgl2Component.components[this.id] = this;
  }

  public getModelMatrix() {
    if (!this._modelMatrix) {
      throw new Error(
        `You are trying to get model matrix of ${
          Object.getPrototypeOf(this)?.constructor?.name
        } component while it wasn't calculated yet`,
      );
    }

    return this._modelMatrix;
  }

  public calcModelMatrix(parentMatrix?: Float32Array, forceCalc = false) {
    const parent = this.parent;

    if (parent && !parentMatrix) {
      throw new Error(
        `You are trying to calc model matrix of ${
          Object.getPrototypeOf(this)?.constructor?.name
        } component but didn't provide parentMatrix. It seems like you've broken render flow`,
      );
    }

    if (this._modelMatrix && !this._shouldCalcModel && !forceCalc) {
      return this.forEachChildren((child) => {
        child.calcModelMatrix(this._parentMatrix, forceCalc);
      });
    }

    this._shouldCalcModel = false;

    const styleScale = this.style.getScaleVec2();

    const translation = this.transform.translation.clone();
    const styleTBLRTranslation = this.style.getTBLRTranslationVec2(this);
    const styleTransformTranslation = this.style
      .getTranslateTranslationVec2(this)
      .multiply(styleScale);

    translation.add(styleTBLRTranslation).add(styleTransformTranslation);

    const selfMat = Mat3.identity()
      .translate(translation)
      .rotate(this.transform.rotation.getRadians())
      .scale(styleScale);

    // Note: scaling size with scale due to we reuse static 2d mesh for rect.
    const scale = this.transform.size.clone().multiply(this.transform.scale);

    if (this.hasChildren) {
      const selfParentMatrix = selfMat.scaleNew(scale.getSign(1));
      if (parentMatrix) selfParentMatrix.multiplyFrom(parentMatrix);
      this._parentMatrix = selfParentMatrix.mat;
    }

    if (parentMatrix) selfMat.multiplyFrom(parentMatrix);
    selfMat.scale(scale);
    this._modelMatrix = selfMat.mat;

    this.forEachChildren((child) => {
      child.calcModelMatrix(this._parentMatrix, true);
    });
  }

  public setParent(parent: Webgl2Component<any, any>): boolean {
    if (parent === this._parent || parent === this) return false;

    if (this._parent) {
      const parent = this._parent;
      this._parent._children.delete(this);
      this._parent = null;
      parent._forceChange();
    }

    parent._children.add(this);
    this._parent = parent;

    this._forceChange();
    parent._forceChange();

    return true;
  }
  public removeParent(): boolean {
    if (!this._parent) return false;

    const parent = this._parent;

    this._parent._children.delete(this);
    this._parent = null;

    this._forceChange();
    parent._forceChange();

    return true;
  }
  public appendChild(child: Webgl2Component<any, any>): boolean {
    if (this._children.has(child) || child === this) return false;

    if (child._parent) {
      child._parent._children.delete(child);
      child._parent._forceChange();
      child._parent = null;
    }

    child._parent = this;
    this._children.add(child);

    child._forceChange();
    this._forceChange();

    return true;
  }
  public removeChild(child: Webgl2Component<any, any>): boolean {
    if (!this._children.has(child)) return false;

    child._parent = null;
    this._children.delete(child);

    child._forceChange();
    this._forceChange();

    return true;
  }

  public forEachChildren(callback: (child: Webgl2Component<any, any>) => void) {
    this._children.forEach(callback);
  }

  public onMouseDown?(e: MouseEvent | TouchEvent): void;
  public onMouseMove?(e: MouseEvent | TouchEvent): void;
  public onMouseUp?(e: MouseEvent | TouchEvent): void;

  public abstract getRenderData(props: P): Webgl2ComponentGetRenderDataOutput;

  @action
  protected _forceChange() {
    this._forceChangeToggle = !this._forceChangeToggle;
  }

  private static _id = 0;
  public static components: NumericDictionary<Webgl2Component<any, any>> = {};
}
